import { Rect } from "../../common/rect.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { ImgID } from "../../image_loader/img_id.js";
import { UIElement } from "./ui_element.js";

export class UICountryFlag extends UIElement
{
  private readonly flag_size_to_holder_size_ratio: number = 0.9;
  private readonly border_radius_ratio: number = 0.1;

  private flag_image: ImageBitmap;
  private flag_image_holder: Rect;
  private flag_img_id: ImgID;
  private dom_last_key = "";

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    image_flag_id: ImgID = ImgID.Flag_unknown,
  )
  {
    super( ctx, x, y, width, height, false, false );
    this.flag_img_id = image_flag_id;

    if ( ImagesHolder.isReady( image_flag_id ) )
    {
      this.flag_image = ImagesHolder.getImage( image_flag_id );
    }
    else
    {
      this.flag_image = ImagesHolder.getImage( ImgID.Flag_unknown );
      void ImagesHolder.ensure( image_flag_id )
        .then( image =>
        {
          this.flag_image = image;
          this.setShouldBeRendered();
        } )
        .catch( () => {} );
    }

    const holder_width = width * this.flag_size_to_holder_size_ratio;
    const holder_height = height * this.flag_size_to_holder_size_ratio;
    this.flag_image_holder = new Rect(
      -holder_width / 2,
      -holder_height / 2,
      holder_width,
      holder_height
    );
  }

  protected override syncDomContent( _node: HTMLElement ): boolean
  {
    const url = ImagesHolder.getUrl( this.flag_img_id );
    if ( !url )
      return false;

    const element_width = this.rect.getW();
    const element_height = this.rect.getH();
    const border_radius = Math.min( element_width, element_height ) * this.border_radius_ratio;
    const holder = this.flag_image_holder;
    const key = `${url}|${holder.getW()}|${holder.getH()}|${border_radius}`;
    if ( key === this.dom_last_key )
      return true;
    this.dom_last_key = key;

    const s = this.getDomSkin().style;
    s.left = `${holder.getX() + element_width / 2}px`;
    s.top = `${holder.getY() + element_height / 2}px`;
    s.width = `${holder.getW()}px`;
    s.height = `${holder.getH()}px`;
    s.borderRadius = `${border_radius}px`;
    s.backgroundImage = `url("${url}")`;
    s.backgroundSize = "100% 100%";
    s.backgroundRepeat = "no-repeat";
    return true;
  }

  render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();

    const element_width = this.rect.getW();
    const element_height = this.rect.getH();
    const border_radius = Math.min( element_width, element_height ) * this.border_radius_ratio;

    this.clipToRoundedImageHolder( border_radius );
    this.drawFlagImage();
    this.ctx.restore();

    this.finishRender();
  }

  private clipToRoundedImageHolder( border_radius: number ): void
  {
    const holder = this.flag_image_holder;
    this.ctx.save();
    this.ctx.beginPath();

    const x1 = holder.getX();
    const y1 = holder.getY();
    const x2 = x1 + holder.getW();
    const y2 = y1 + holder.getH();
    this.ctx.moveTo( x1 + border_radius, y1 );
    this.ctx.lineTo( x2 - border_radius, y1 );
    this.ctx.arcTo( x2, y1, x2, y1 + border_radius, border_radius );
    this.ctx.lineTo( x2, y2 - border_radius );
    this.ctx.arcTo( x2, y2, x2 - border_radius, y2, border_radius );
    this.ctx.lineTo( x1 + border_radius, y2 );
    this.ctx.arcTo( x1, y2, x1, y2 - border_radius, border_radius );
    this.ctx.lineTo( x1, y1 + border_radius );
    this.ctx.arcTo( x1, y1, x1 + border_radius, y1, border_radius );

    this.ctx.clip();
  }

  private drawFlagImage(): void
  {
    this.ctx.drawImage(
      this.flag_image,
      this.flag_image_holder.getX(),
      this.flag_image_holder.getY(),
      this.flag_image_holder.getW(),
      this.flag_image_holder.getH()
    );
  }

  name(): string
  {
    return "UICountryFlag";
  }

  setImageFlagById( image_flag_id: ImgID ): void
  {
    if ( image_flag_id === this.flag_img_id )
      return;

    this.flag_img_id = image_flag_id;
    this.setShouldBeRendered();
    // The DOM backend shows the flag by URL; only the canvas path needs the decoded image.
    if ( this.dom_mode )
      return;

    if ( ImagesHolder.isReady( image_flag_id ) )
    {
      this.flag_image = ImagesHolder.getImage( image_flag_id );
      return;
    }
    this.flag_image = ImagesHolder.getImage( ImgID.Flag_unknown );
    void ImagesHolder.ensure( image_flag_id )
      .then( image =>
      {
        if ( this.flag_img_id !== image_flag_id ) return;
        this.flag_image = image;
        this.setShouldBeRendered();
      } )
      .catch( () => {} );
  }
}
